import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { EventsCollectionQueryType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { getSportStartTime } from 'src/components/CustomersBar/EventsTab/utils';
import { EventModel, MARKET_TYPE } from 'src_common/common/websocket2/models/EventModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { AvailableItemType } from 'src/components/Filters/FilterTabs';
import { DateTime } from 'src_common/utils/time/time';
import { DayFilterState } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/DayFilter.state';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';

export type SortDirectionType = 'asc' | 'default';

export function sortSelections(selections: Array<SelectionModel>, sort: SortDirectionType): Array<SelectionModel> {
    if (sort === 'asc') {
        selections
            .sort((selectionA, selectionB): number => (selectionA.name < selectionB.name ? -1 : 1))
            .sort((selectionA, selectionB): number => {
                const priceA = selectionA.price?.d;
                const priceB = selectionB.price?.d;
                if (priceA !== undefined && priceB !== undefined) {
                    return priceB - priceA;
                }
                if (priceA === undefined) {
                    return 1;
                } else {
                    return -1;
                }
            });
    } else {
        selections.sort((selectionA, selectionB) => {
            // eslint-disable-next-line no-restricted-globals
            const numberA = typeof selectionA.metaDataNumber === 'string' ? Number(selectionA.metaDataNumber) : null;
            // eslint-disable-next-line no-restricted-globals
            const numberB = typeof selectionB.metaDataNumber === 'string' ? Number(selectionB.metaDataNumber) : null;
            if (numberA !== null && numberB !== null && !isNaN(numberA) && !isNaN(numberB)) {
                return numberA < numberB ? 1 : -1;
            }
            return 0;
        });
    }

    selections
        .sort((selection) => (selection.templateId === 'unnamed-favourite' ? -1 : 0))
        .sort((selection) => (selection.resultType === 'void' ? -1 : 0));

    return selections.reverse();
}

export const QUICKPICK_ROUTER_NAMESPACE = 'customers';

class MarketFilterState {
    @observable private marketType: MARKET_TYPE;

    public constructor(
        private readonly getEvents: () => EventModel | null,
        private readonly competitionFilterState: CompetitionFilterState,
        private readonly appState: AppState
    ) {
        this.marketType = MARKET_TYPE.RACE_WINNER;
        makeAutoObservable(this);
    }

    @computed public get getMarketType(): MARKET_TYPE {
        return this.marketType;
    }

    @action public setMainMarket = (): void => {
        this.marketType = MARKET_TYPE.RACE_WINNER;
    };

    @action public setForecastMarket = (): void => {
        this.marketType = MARKET_TYPE.FORECAST;
    };

    @action public setExtraPlacesMarket = (): void => {
        this.marketType = MARKET_TYPE.EXTRA_PLACES;
    };

    @action public setMatchBetMarket = (): void => {
        this.marketType = MARKET_TYPE.MATCH_BET;
    };

    @action public setSubRaceMarket = (): void => {
        this.marketType = MARKET_TYPE.SUB_RACE;
    };

    @action public setWithoutMarket = (): void => {
        this.marketType = MARKET_TYPE.WITHOUT;
    };

    @action public setWinOnlyMarke = (): void => {
        this.marketType = MARKET_TYPE.WIN_ONLY;
    };

    @action public setLengthenTheOddsMarket = (): void => {
        this.marketType = MARKET_TYPE.LENGTHEN_THE_ODDS;
    };

    @action public setTop2Finish = (): void => {
        this.marketType = MARKET_TYPE.TOP_2_FINISH;
    };

    @action public setTop3Finish = (): void => {
        this.marketType = MARKET_TYPE.TOP_3_FINISH;
    };

    @action public setTop4Finish = (): void => {
        this.marketType = MARKET_TYPE.TOP_4_FINISH;
    };

    @action public setTop5Finish = (): void => {
        this.marketType = MARKET_TYPE.TOP_5_FINISH;
    };

    @action public setTop6Finish = (): void => {
        this.marketType = MARKET_TYPE.TOP_6_FINISH;
    };

    @action public setMarket = (market: string): void => {
        switch (market) {
            case MARKET_TYPE.RACE_WINNER:
                this.setMainMarket();
                break;
            case MARKET_TYPE.FORECAST:
                this.setForecastMarket();
                break;
            case MARKET_TYPE.EXTRA_PLACES:
                this.setExtraPlacesMarket();
                break;
            case MARKET_TYPE.MATCH_BET:
                this.setMatchBetMarket();
                break;
            case MARKET_TYPE.SUB_RACE:
                this.setSubRaceMarket();
                break;
            case MARKET_TYPE.WITHOUT:
                this.setWithoutMarket();
                break;
            case MARKET_TYPE.WIN_ONLY:
                this.setWinOnlyMarke();
                break;
            case MARKET_TYPE.LENGTHEN_THE_ODDS:
                this.setLengthenTheOddsMarket();
                break;
            case MARKET_TYPE.TOP_2_FINISH:
                this.setTop2Finish();
                break;
            case MARKET_TYPE.TOP_3_FINISH:
                this.setTop2Finish();
                break;
            case MARKET_TYPE.TOP_4_FINISH:
                this.setTop2Finish();
                break;
            case MARKET_TYPE.TOP_5_FINISH:
                this.setTop2Finish();
                break;
            case MARKET_TYPE.TOP_6_FINISH:
                this.setTop2Finish();
                break;
            default:
                console.log('No matching market name! Default set main market');
                this.setMainMarket();
        }
    };

    @computed public get marketsForSelectedMarket(): Array<MarketModel> | null {
        if (this.getEvents() === null) {
            return null;
        }
        switch (this.getMarketType) {
            case MARKET_TYPE.LENGTHEN_THE_ODDS:
                return this.getEvents()?.marketLengthenTheOdds ?? null;
            case MARKET_TYPE.RACE_WINNER:
                return this.getEvents()?.marketRaceWinner ?? null;
            case MARKET_TYPE.FORECAST:
                return this.getEvents()?.marketRaceWinner ?? null;
            case MARKET_TYPE.EXTRA_PLACES:
                return this.getEvents()?.marketExtraPlaces ?? null;
            case MARKET_TYPE.MATCH_BET:
                return this.getEvents()?.marketMatchBet ?? null;
            case MARKET_TYPE.SUB_RACE:
                return this.getEvents()?.marketSubRace ?? null;
            case MARKET_TYPE.WITHOUT:
                return this.getEvents()?.marketWithout ?? null;
            case MARKET_TYPE.WIN_ONLY: {
                const marketWinOnly = this.getEvents()?.marketWinOnly;
                return marketWinOnly !== null && marketWinOnly !== undefined ? [marketWinOnly] : null;
            }
            case MARKET_TYPE.TOP_2_FINISH:
            case MARKET_TYPE.TOP_3_FINISH:
            case MARKET_TYPE.TOP_4_FINISH:
            case MARKET_TYPE.TOP_5_FINISH:
            case MARKET_TYPE.TOP_6_FINISH:
                return this.getEvents()?.marketTopFinish ?? null;
        }
    }

    @computed public get marketModelForSelectedMarket(): MarketModel | null {
        return this.marketsForSelectedMarket !== null && this.marketsForSelectedMarket[0] !== undefined
            ? this.marketsForSelectedMarket[0]
            : null;
    }

    @computed public get raceWinnerMarket(): MarketModel | null {
        return this.getEvents()?.marketRaceWinner[0] ?? null;
    }

    @computed public get availableMarketsNameForSelectedEvent(): Array<AvailableItemType> {
        const out: Array<{ id: MARKET_TYPE; name: string }> = [];
        if (this.competitionFilterState.selectedEventIdFromURL === null) {
            return out;
        }
        const event = this.appState.websocketV2.getEvent(this.competitionFilterState.selectedEventIdFromURL);

        if (event === null) {
            return out;
        }

        out.push(
            { id: MARKET_TYPE.RACE_WINNER, name: 'Main markets' },
            { id: MARKET_TYPE.FORECAST, name: 'Forecast/Tricast' }
        );

        if (event.marketExtraPlaces.length > 0 && event.marketExtraPlaces.some((market) => market.active === true)) {
            out.push({ id: MARKET_TYPE.EXTRA_PLACES, name: 'Extra Places' });
        }

        if (event.marketMatchBet.length > 0 && event.marketMatchBet.some((market) => market.active === true)) {
            out.push({ id: MARKET_TYPE.MATCH_BET, name: 'Match Bet' });
        }

        if (event.marketSubRace.length > 0 && event.marketSubRace.some((market) => market.active === true)) {
            out.push({ id: MARKET_TYPE.SUB_RACE, name: 'Sub Races' });
        }

        if (event.marketWithout.length > 0 && event.marketWithout.some((market) => market.active === true)) {
            out.push({ id: MARKET_TYPE.WITHOUT, name: 'Betting Without' });
        }

        if (
            event.marketLengthenTheOdds.length > 0 &&
            event.marketLengthenTheOdds.some((market) => market.active === true)
        ) {
            out.push({ id: MARKET_TYPE.LENGTHEN_THE_ODDS, name: 'Lengthen The Odds' });
        }

        if (event.marketWinOnly !== null && event.marketWinOnly.active === true) {
            out.push({ id: MARKET_TYPE.WIN_ONLY, name: 'Win Only' });
        }

        if (event.marketTopFinish.length > 0 && event.marketTopFinish.some((market) => market.active === true)) {
            out.push({ id: MARKET_TYPE.TOP_2_FINISH, name: 'To be Placed' });
        }

        return out;
    }
}

class CompetitionFilterState {
    public constructor(
        private readonly appState: AppState,
        private readonly filtredEventsCollection: () => EventsCollectionQueryModel | null
    ) {
        makeAutoObservable(this);
    }

    @action public setSelectedCompetitionIdToURL = (competitionId: string): void => {
        this.appState.routerBackoffice.removeParamInNamespace(QUICKPICK_ROUTER_NAMESPACE, 'event');
        if (competitionId === 'all') {
            this.appState.routerBackoffice.removeParamInNamespace(QUICKPICK_ROUTER_NAMESPACE, 'competition');
        } else {
            this.appState.routerBackoffice.setParamInNamespace(
                QUICKPICK_ROUTER_NAMESPACE,
                'competition',
                competitionId
            );
        }
    };

    @computed public get selectedCompetitionIdFromURL(): string | null {
        return this.appState.routerBackoffice.getParametersString(QUICKPICK_ROUTER_NAMESPACE, 'competition') ?? 'all';
    }

    @computed public get selectedEventIdFromURL(): number | null {
        return this.appState.routerBackoffice.getParametersNumber(QUICKPICK_ROUTER_NAMESPACE, 'event');
    }

    @computed public get allCompetitionForFilters(): Array<AvailableItemType> {
        const eventList = this.filtredEventsCollection();
        const out: Array<AvailableItemType> = [{ id: 'all', name: 'All' }];
        if (eventList === null) {
            return out;
        }
        return out.concat(eventList.competitionForView.map((item) => ({ id: item.id.toString(), name: item.name })));
    }
}

export class QuickPickViewState {
    @observable public sort: SortDirectionType = 'asc';
    @observable public hoveredEventId: number | null = null;
    @observable public hoveredSelectionId: number | null = null;

    @observable public readonly marketFilterState: MarketFilterState;
    @observable public readonly timeFilterState: DayFilterState;
    @observable public readonly competitionFilterState: CompetitionFilterState;

    public constructor(
        private readonly appState: AppState,
        private readonly sport: string
    ) {
        this.timeFilterState = new DayFilterState(this.allEventsList, appState);
        this.competitionFilterState = new CompetitionFilterState(
            appState,
            () => this.timeFilterState.eventsFromCurrentTimeFilter
        );
        this.marketFilterState = new MarketFilterState(
            () => this.getSelectedEvent,
            this.competitionFilterState,
            appState
        );
        this.sort = sport === 'horseracing' || sport === 'internationalhorseracing' ? 'asc' : 'default';
        makeAutoObservable(this);
    }

    private allEventsList = (): EventsCollectionQueryModel | null => {
        const query: EventsCollectionQueryType = {
            sport: [this.sport],
            'startTime[from]': getSportStartTime(this.sport),
            'startTime[to]': DateTime.current().startOfDays().addDays(6).endOfDays().toISOString(),
            filterMarketStatus: {
                noteq: ['settled', 'new'],
            },
        };

        return this.appState.websocketV2.getEventQuery(query);
    };

    @action public onHoverEventHandler = (eventId: number): void => {
        this.hoveredEventId = eventId;
    };

    @action public onHoverSelectionHandler = (eventId: number, selectionId: number): void => {
        this.onHoverEventHandler(eventId);
        this.hoveredSelectionId = selectionId;
    };

    @action public onSetSort = (): void => {
        this.sort = this.sort === 'asc' ? 'default' : 'asc';
    };

    @action public onCloseRacecard = (): void => {
        this.marketFilterState.setMainMarket();
        this.setSelectedEventIdToURL(null);
        this.appState.routerBackoffice.removeParamInNamespace(QUICKPICK_ROUTER_NAMESPACE, 'event');
    };

    @action public setSelectedEventIdToURL = (eventId: string | null): void => {
        this.marketFilterState.setMainMarket();
        if (eventId === null) {
            this.appState.routerBackoffice.removeParamInNamespace(QUICKPICK_ROUTER_NAMESPACE, 'event');
        } else {
            this.appState.routerBackoffice.setParamInNamespace(QUICKPICK_ROUTER_NAMESPACE, 'event', eventId);
        }
    };

    @computed public get filteredEventsCollectionViaUrl(): EventsCollectionQueryModel | null {
        let out: EventsCollectionQueryModel | null;
        if (
            this.competitionFilterState.selectedCompetitionIdFromURL !== null &&
            this.competitionFilterState.selectedCompetitionIdFromURL !== 'all' &&
            this.timeFilterState.eventsFromCurrentTimeFilter !== null
        ) {
            out = this.timeFilterState.eventsFromCurrentTimeFilter.filterByCompetition(
                (competitionId) =>
                    competitionId === parseFloat(this.competitionFilterState.selectedCompetitionIdFromURL ?? '')
            );
        } else {
            out = this.timeFilterState.eventsFromCurrentTimeFilter;
        }

        return out;
    }

    @computed public get filteredEventsId(): Array<number> {
        return this.filteredEventsCollectionViaUrl?.ids ?? [];
    }

    @computed public get getSelectedEvent(): EventModel | null {
        const firstEventFromList = this.getEventsList[0];

        if (firstEventFromList === undefined) {
            return null;
        }

        const selectedEvent = this.competitionFilterState.selectedEventIdFromURL;
        const hoveredEvent = this.hoveredEventId;

        // eslint-disable-next-line no-negated-condition
        return selectedEvent !== null
            ? this.appState.websocketV2.getEvent(selectedEvent)
            : hoveredEvent === null
              ? firstEventFromList
              : this.appState.websocketV2.getEvent(hoveredEvent);
    }

    @computed public get getEventsList(): Array<EventModel> {
        const output: Array<EventModel> = [];

        this.filteredEventsId.forEach((eventId) => {
            const eventModel = this.appState.websocketV2.getEvent(eventId);
            if (eventModel !== null && eventModel.marketsAll.length > 0) {
                output.push(eventModel);
            }
        });

        output.sort((eventA, eventB) => {
            const a = DateTime.from(eventA.timeSettingsStartTime)?.unixMs();
            const b = DateTime.from(eventB.timeSettingsStartTime)?.unixMs();
            if (a === undefined || b === undefined) {
                return 0;
            }
            return a - b;
        });
        return output;
    }

    @computed public get isLoading(): boolean {
        return this.allEventsList() === null;
    }
}
